import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, AlertCircle, AlertTriangle, Info, CheckCircle, Clock, Calendar } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../store/authStore';
import { getAlerts, resolveAlert } from '../../api/alerts';
import { getSession } from '../../api/sessions';
import { SkeletonBlock } from '../../components/SkeletonBlock';
import { Button } from '../../components/Button';
interface Alert {
  id: string;
  sessionId: string;
  type: string;
  message: string;
  severity: 'low' | 'medium' | 'high' | 'critical';
  status: 'active' | 'resolved';
  triggeredAt: string;
  resolvedAt: string | null;
}
export function AlertDetail() {
  const {
    alertId
  } = useParams<{
    alertId: string;
  }>();
  const navigate = useNavigate();
  const {
    token
  } = useAuth();
  const [alert, setAlert] = useState<Alert | null>(null);
  const [session, setSession] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(false);
  const [isResolving, setIsResolving] = useState(false);
  const [showSuccessAnim, setShowSuccessAnim] = useState(false);
  useEffect(() => {
    const fetchData = async () => {
      if (!token || !alertId) return;
      setIsLoading(true);
      setError(false);
      try {
        // Fetch alerts to find the specific one
        const alertsRes = await getAlerts(token);
        const foundAlert = alertsRes.find((a: Alert) => a.id === alertId);
        if (!foundAlert) {
          throw new Error('Alert not found');
        }
        setAlert(foundAlert);
        // Fetch related session
        const sessionRes = await getSession(token, foundAlert.sessionId);
        setSession(sessionRes);
      } catch (err) {
        // console.error('Failed to fetch alert details', err)
        setError(true);
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [token, alertId]);
  const handleResolve = async () => {
    if (!token || !alert) return;
    setIsResolving(true);
    try {
      await resolveAlert(token, alert.id);
      // Show success animation
      setShowSuccessAnim(true);
      // Update local state after a short delay for animation
      setTimeout(() => {
        setAlert({
          ...alert,
          status: 'resolved',
          resolvedAt: new Date().toISOString()
        });
        setShowSuccessAnim(false);
      }, 1500);
    } catch (err) {
      // console.error('Failed to resolve alert', err)
    } finally {
      setIsResolving(false);
    }
  };
  if (isLoading) {
    return <main className="w-full max-w-[800px] mx-auto pb-20 md:pb-0" data-id="element-326" data-mp="2koxm8er4c1nd">
        <div className="flex items-center mb-8" data-id="element-327" data-mp="2pn8w2pheyc5l">
          <SkeletonBlock width="40px" height="40px" borderRadius="50%" className="mr-4" data-id="element-328" data-mp="2plxring590df" />
          <SkeletonBlock width="150px" height="24px" data-id="element-329" data-mp="3i5mvuumb887b" />
        </div>
        <div className="flex flex-col items-center mb-8" data-id="element-330" data-mp="16pcmv7py1rdt">
          <SkeletonBlock width="80px" height="80px" borderRadius="50%" className="mb-4" data-id="element-331" data-mp="16nrrxgzpw6ff" />
          <SkeletonBlock width="120px" height="20px" className="mb-2" data-id="element-332" data-mp="1gfxhyl6esdrz" />
          <SkeletonBlock width="180px" height="16px" data-id="element-333" data-mp="1gh8min7ohpk5" />
        </div>
        <div className="space-y-6" data-id="element-334" data-mp="1bguolxzsjvzu">
          <SkeletonBlock height="200px" borderRadius="20px" data-id="element-335" data-mp="nqqklpioxu8b" />
          <SkeletonBlock height="150px" borderRadius="20px" data-id="element-336" data-mp="nra2j68ccimc" />
        </div>
      </main>;
  }
  if (error || !alert) {
    return <main className="w-full max-w-[800px] mx-auto pb-20 md:pb-0" data-id="element-337" data-mp="iu026vjeymjq">
        <div className="flex items-center mb-8" data-id="element-338" data-mp="wszqu36xf1fw">
          <button onClick={() => navigate('/app/alerts')} className="w-[40px] h-[40px] rounded-full flex items-center justify-center hover:bg-border/50 transition-colors mr-4" aria-label="Go back" data-id="element-339" data-mp="spblzs9oijyc">
            <ArrowLeft size={24} className="text-text-primary" data-id="element-340" data-mp="244goav5weghf" />
          </button>
          <h1 className="text-[20px] font-semibold text-text-primary" data-id="element-341" data-mp="2zghx6v5kkyvu">
            Alert Detail
          </h1>
        </div>
        <div className="bg-danger/10 border border-danger/20 rounded-[16px] p-6 text-center" data-id="element-342" data-mp="33h1t5veepwge">
          <AlertTriangle size={32} className="text-danger mx-auto mb-3" data-id="element-343" data-mp="38ejscu2mgxb7" />
          <h3 className="text-[16px] font-semibold text-text-primary mb-2" data-id="element-344" data-mp="3dfzc8it2cxs0">
            Failed to load alert
          </h3>
          <p className="text-[14px] text-text-secondary mb-4" data-id="element-345" data-mp="3idskdhdxg1ux">
            The alert could not be found or there was a connection error.
          </p>
          <Button onClick={() => navigate('/app/alerts')} className="mx-auto" data-id="element-346" data-mp="3r5uiva4fr82k">
            Back to Alerts
          </Button>
        </div>
      </main>;
  }
  const formatType = (type: string) => {
    return type.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
  };
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    }) + ' at ' + date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit'
    });
  };
  const getSeverityStyles = (severity: string, status: string) => {
    if (status === 'resolved') {
      return {
        bg: 'bg-success',
        color: 'text-success',
        Icon: CheckCircle,
        label: 'Resolved'
      };
    }
    switch (severity) {
      case 'high':
      case 'critical':
        return {
          bg: 'bg-danger',
          color: 'text-danger',
          Icon: AlertCircle,
          label: 'High Severity'
        };
      case 'medium':
        return {
          bg: 'bg-[#F59E0B]',
          color: 'text-[#F59E0B]',
          Icon: AlertTriangle,
          label: 'Medium Severity'
        };
      case 'low':
      default:
        return {
          bg: 'bg-success',
          color: 'text-success',
          Icon: Info,
          label: 'Low Severity'
        };
    }
  };
  const styles = getSeverityStyles(alert.severity, alert.status);
  const Icon = styles.Icon;
  return <main className="w-full max-w-[800px] mx-auto pb-20 md:pb-0 animate-fadeInUp" data-id="element-347" data-mp="fi5qo2a1snyy">
      {/* Header */}
      <div className="flex items-center justify-between mb-8" data-id="element-348" data-mp="kgz2lq9os6x0">
        <div className="flex items-center" data-id="element-349" data-mp="6iqxuw6es1ba">
          <button onClick={() => navigate('/app/alerts')} className="w-[40px] h-[40px] rounded-full flex items-center justify-center hover:bg-border/50 transition-colors mr-2" aria-label="Go back" data-id="element-350" data-mp="6gxrlhzpjj3t">
            <ArrowLeft size={24} className="text-text-primary" data-id="element-351" data-mp="1j5zonz2bwzm" />
          </button>
          <h1 className="text-[20px] font-semibold text-text-primary" data-id="element-352" data-mp="1l8y9wea6yfy">
            Alert Detail
          </h1>
        </div>

        {alert.status === 'active' ? <button onClick={handleResolve} disabled={isResolving} className="border border-danger text-danger rounded-pill px-[16px] py-[6px] text-[14px] font-medium hover:bg-danger/5 transition-colors disabled:opacity-50" data-id="element-353" data-mp="1vwx5fce9twps">
            {isResolving ? 'Resolving...' : 'Resolve'}
          </button> : <div className="bg-success/20 text-success rounded-pill px-[16px] py-[6px] text-[14px] font-medium" data-id="element-354" data-mp="3le1390qshjt8">
            Resolved
          </div>}
      </div>

      {/* Severity Hero */}
      <div className="flex flex-col items-center mb-10" data-id="element-355" data-mp="3u74m5ok7eov9">
        <motion.div animate={{
        backgroundColor: alert.status === 'resolved' ? '#00D4D4' : styles.bg.replace('bg-', '')
      }} className={`w-[80px] h-[80px] rounded-full flex items-center justify-center mb-4 ${styles.bg}`} data-id="element-356" data-mp="3u6mm0guf7qi2">
          <Icon size={36} className="text-white" data-id="element-357" data-mp="2erysy1jp8oo8" />
        </motion.div>
        <h2 className={`text-[18px] font-semibold mb-1 ${styles.color}`} data-id="element-358" data-mp="29sm09065cndq">
          {styles.label}
        </h2>
        <p className="text-[14px] text-text-secondary" data-id="element-359" data-mp="1nvrdi709jtav">
          {formatType(alert.type)} Detected
        </p>
      </div>

      <div className="space-y-6" data-id="element-360" data-mp="1xnonjafjcf80">
        {/* Info Card */}
        <div className="bg-white rounded-[20px] shadow-card p-5" data-id="element-361" data-mp="1xo6ryuccpzr0">
          <div className="space-y-4" data-id="element-362" data-mp="22n1cc0pyvwd8">
            <div className="flex justify-between items-center" data-id="element-363" data-mp="22lyjhn933juo">
              <span className="text-[14px] text-text-secondary" data-id="element-364" data-mp="1501acgglgqve">
                Alert Type
              </span>
              <span className="text-[14px] font-medium text-text-primary" data-id="element-365" data-mp="19yvqtbr3a3cc">
                {formatType(alert.type)}
              </span>
            </div>
            <div className="h-[1px] w-full bg-border" data-id="element-366" data-mp="1iyite0vk1f6z" />

            <div className="flex justify-between items-center" data-id="element-367" data-mp="1j0by3amlxpcc">
              <span className="text-[14px] text-text-secondary" data-id="element-368" data-mp="2qd3yy1b44a4g">
                Triggered at
              </span>
              <span className="text-[14px] font-medium text-text-primary text-right max-w-[200px]" data-id="element-369" data-mp="2ucmlt2mhuv9x">
                {formatDate(alert.triggeredAt)}
              </span>
            </div>
            <div className="h-[1px] w-full bg-border" data-id="element-370" data-mp="eoqvv34q05oh" />

            <div className="flex justify-between items-center" data-id="element-371" data-mp="erd1uybyqd4l">
              <span className="text-[14px] text-text-secondary" data-id="element-372" data-mp="9wpjzibgsgyz">
                Resolved at
              </span>
              <span className={`text-[14px] font-medium text-right max-w-[200px] ${alert.resolvedAt ? 'text-text-primary' : 'text-text-secondary'}`} data-id="element-373" data-mp="4yxjhzwr7mj6">
                {alert.resolvedAt ? formatDate(alert.resolvedAt) : 'Not yet resolved'}
              </span>
            </div>
            <div className="h-[1px] w-full bg-border" data-id="element-374" data-mp="3i68zszhdgsjm" />

            <div className="flex justify-between items-center" data-id="element-375" data-mp="3i7k6b727ceef">
              <span className="text-[14px] text-text-secondary" data-id="element-376" data-mp="1lce9ohg3ru3h">Status</span>
              {alert.status === 'active' ? <div className="px-3 py-1 bg-danger/15 text-danger text-[12px] font-semibold rounded-pill" data-id="element-377" data-mp="1gevzyou9hauf">
                  Active
                </div> : <div className="px-3 py-1 bg-success/15 text-success text-[12px] font-semibold rounded-pill" data-id="element-378" data-mp="1dcyozt7hmizz">
                  Resolved
                </div>}
            </div>
            <div className="h-[1px] w-full bg-border" data-id="element-379" data-mp="1megcvxr4zoz8" />

            <div className="flex justify-between items-center" data-id="element-380" data-mp="1mcviq98qi81w">
              <span className="text-[14px] text-text-secondary" data-id="element-381" data-mp="ypnc9nzkksya">Severity</span>
              <div className={`px-3 py-1 text-[12px] font-semibold rounded-pill ${alert.severity === 'high' || alert.severity === 'critical' ? 'bg-danger/15 text-danger' : alert.severity === 'medium' ? 'bg-[#F59E0B]/15 text-[#F59E0B]' : 'bg-success/15 text-success'}`} data-id="element-382" data-mp="uojxl4t9d2yn">
                {alert.severity.charAt(0).toUpperCase() + alert.severity.slice(1)}
              </div>
            </div>
          </div>
        </div>

        {/* Message Card */}
        <div className="bg-white rounded-[20px] shadow-card p-5" data-id="element-383" data-mp="2mmmut2e1y63u">
          <h3 className="text-[16px] font-semibold text-text-primary mb-3" data-id="element-384" data-mp="2rdlaq0v4p26k">
            Alert Message
          </h3>
          <p className="text-[15px] text-text-primary leading-[1.7]" data-id="element-385" data-mp="2dmq8v8j1rlch">
            {alert.message}
          </p>
        </div>

        {/* Related Session Card */}
        {session && <div className="bg-white rounded-[20px] shadow-card p-5" data-id="element-386" data-mp="36b5b0uh2y96e">
            <h3 className="text-[16px] font-semibold text-text-primary mb-4" data-id="element-387" data-mp="368rh4oy4z30f">
              From Session
            </h3>

            <div className="flex items-center gap-4 mb-6" data-id="element-388" data-mp="2wcogjnixncfl">
              <div className="w-[48px] h-[48px] bg-primary/10 rounded-full flex items-center justify-center" data-id="element-389" data-mp="2wc6eubdi460a">
                <Calendar size={24} className="text-primary" data-id="element-390" data-mp="31dvp3oswgfnb" />
              </div>
              <div data-id="element-391" data-mp="1kvy6ftbe20gp">
                <p className="text-[15px] font-medium text-text-primary" data-id="element-392" data-mp="1kzm2az4b2yyl">
                  {new Date(session.startedAt).toLocaleDateString('en-US', {
                weekday: 'long',
                day: 'numeric',
                month: 'long',
                year: 'numeric'
              })}
                </p>
                <p className="text-[14px] text-text-secondary mt-1 flex items-center gap-1" data-id="element-393" data-mp="x1o0keao3t1l">
                  <Clock size={14} data-id="element-394" data-mp="s3cv0freo4p6" />
                  {Math.round((new Date(session.endedAt).getTime() - new Date(session.startedAt).getTime()) / 60000)}{' '}
                  min duration
                </p>
              </div>
            </div>

            <button onClick={() => navigate(`/app/history/${session.id}`)} className="w-full h-[48px] border border-primary text-primary rounded-[14px] font-medium text-[15px] hover:bg-primary/5 transition-colors" data-id="element-395" data-mp="e44p740qlyc0">
              View Session
            </button>
          </div>}

        {/* Resolve Action Section */}
        <AnimatePresence mode="wait" data-id="element-396" data-mp="2b2apsvxioucx">
          {alert.status === 'active' && !showSuccessAnim && <motion.div key="resolve-btn" initial={{
          opacity: 1,
          scale: 1
        }} exit={{
          opacity: 0,
          scale: 0.9
        }} className="pt-4" data-id="element-397" data-mp="2g0c5r7zncdk6">
              <button onClick={handleResolve} disabled={isResolving} className="w-full h-[52px] bg-danger text-white rounded-[14px] font-medium text-[16px] flex items-center justify-center transition-opacity disabled:opacity-70 mb-3" data-id="element-398" data-mp="1s36l1zd0dmr2">
                {isResolving ? <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" data-id="element-399" data-mp="263h8stj0ul80" /> : <>
                    <CheckCircle size={20} className="mr-2" data-id="element-400" data-mp="3hhl360tiubdl" />
                    Mark as Resolved
                  </>}
              </button>
              <p className="text-[13px] text-text-secondary text-center" data-id="element-401" data-mp="3g0xuiba589gk">
                Resolving this alert marks it as reviewed.
              </p>
            </motion.div>}

          {showSuccessAnim && <motion.div key="success-anim" initial={{
          opacity: 0,
          scale: 0.8
        }} animate={{
          opacity: 1,
          scale: 1
        }} className="pt-4 flex flex-col items-center justify-center py-6" data-id="element-402" data-mp="3pwiqnssj6fie">
              <div className="relative w-[64px] h-[64px] mb-4" data-id="element-403" data-mp="ck8mslytqtpb">
                <motion.div initial={{
              scale: 0
            }} animate={{
              scale: 1
            }} transition={{
              type: 'spring',
              stiffness: 200,
              damping: 15
            }} className="absolute inset-0 bg-success rounded-full flex items-center justify-center z-10" data-id="element-404" data-mp="7kvzk041jkm7">
                  <CheckCircle size={32} className="text-white" data-id="element-405" data-mp="155npml6ho2si" />
                </motion.div>
                <motion.div initial={{
              scale: 0.8,
              opacity: 1
            }} animate={{
              scale: 1.5,
              opacity: 0
            }} transition={{
              duration: 1,
              ease: 'easeOut'
            }} className="absolute inset-0 bg-success rounded-full z-0" data-id="element-406" data-mp="r6ecxvhhrqta" />
              </div>
              <p className="text-[16px] font-medium text-success" data-id="element-407" data-mp="cib3gc48pwzq">
                Alert Resolved
              </p>
            </motion.div>}
        </AnimatePresence>
      </div>
    </main>;
}